
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import { ObjectType, TitleListType } from '@/@types/graphql-types';
import AdUnit from '@/components/AdUnit.vue';
import LoginButton from '@/components/LoginButton.vue';
import QuickActionBar from '@/components/QuickActionBar.vue';
import TitleInfo from '@/components/details/TitleInfo.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { QuickActionBarProps } from '@/interfaces/components/QuickActionBar';
import { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import { TitleListName } from '@/interfaces/responses/title-list';
import { TitleContentType } from '@/interfaces/snowplow/title-context';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';

@Component({
	name: 'TitleSidebar',
	components: {
		QuickActionBar,
		TitleInfo,
		PosterGraphql,
		AdUnit,
		LoginButton,
	},
})
export default class TitleSidebar extends Vue {
	location = 'TitleSidebar';
	TitleListType = TitleListType;

	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];
	@Prop({ required: true }) title: TitleDetailsNode;
	@Prop() titleContentType: TitleContentType | undefined;

	get isTitleTypeSeason() {
		return this.title.objectType === ObjectType.ShowSeason;
	}

	get ageCertification() {
		return this.isTitleTypeSeason ? this.title.show?.content.ageCertification : this.title.content.ageCertification;
	}

	get productionCountries() {
		return this.isTitleTypeSeason
			? this.title.show?.content.productionCountries
			: this.title.content.productionCountries;
	}

	get externalIds() {
		return this.isTitleTypeSeason ? this.title.show?.content.externalIds : this.title.content.externalIds;
	}

	get posterGraphqlProps(): PosterGraphqlProps {
		return {
			titleId: this.title.id,
			titleObjectId: this.title.objectId,
			titleObjectType: this.title.objectType,
			titleName: this.title.content.title,
			titlePosterUrl: this.title.content.posterUrl ?? '',
			titleBackdrops: this.title.content.backdrops,
			titleScoring: this.title.content.scoring,
			titleAvailableTo: this.title.availableTo,
			titleUpcomingReleases: this.title.content.upcomingReleases,
			seenEpisodeCount: (this.title.seenState || {}).seenEpisodeCount,
			isInCustomList: this.isInCustomList,
			isTvShowTracked: this.isTvShowTracked,
			isInWatchlist: this.isTitleInList(TitleListType.WatchNext),
			isInSeenList: this.isTitleInList(TitleListType.CaughtUp),
			isInLikeList: this.isTitleInList(TitleListType.Likelist),
			isInDislikeList: this.isTitleInList(TitleListType.Dislikelist),
			imageProfile: 'poster_detail',
			noRadius: 'bottom',
			lazy: true,
			showAvailabilityBadge: true,
		};
	}

	get quickActionBarProps(): QuickActionBarProps {
		const props: QuickActionBarProps = {
			titleId: this.title.id,
			titleName: this.title.content.title,
			titleObjectId: this.title.objectId,
			titleObjectType: this.title.objectType,
			titleBackdrops: this.title.content.backdrops,
			titleContentType: this.titleContentType,
			titlePosterUrl: this.title.content.posterUrl ?? '',
			titleSeenEpisodeCount: (this.title.seenState || {}).seenEpisodeCount,
			isInWatchlist: this.isTitleInList(TitleListType.WatchNext),
			isInCustomList: this.isInCustomList,
			isTvShowTracked: this.isTvShowTracked,
			isInSeenList: this.isTitleInList(TitleListType.CaughtUp),
			isInLikeList: this.isTitleInList(TitleListType.Likelist),
			isInDislikeList: this.isTitleInList(TitleListType.Dislikelist),
			seasonNumber: this.title.content.seasonNumber,
			without: [TitleListName.SEENLIST],
			additionalContexts: this.additionalContexts,
			isTitleUpcoming: this.isTitleUpcoming,
			isTitleReleased: this.title.content.isReleased,
		};

		if (this.isShowAvailable) {
			props.showDetails = {
				id: this.title.show?.id as string,
				objectType: this.title.show?.objectType as ObjectType,
				objectId: this.title.show?.objectId as number,
				title: this.title.show?.content.title,
				isReleased: this.title.show?.content.isReleased,
			};
		}

		return props;
	}

	get isTitleUpcoming() {
		return !this.title?.content.isReleased;
	}

	get totalSeasonCount() {
		if (this.isShowAvailable) {
			return this.title.show?.totalSeasonCount;
		}
		return this.title.totalSeasonCount;
	}

	get isInCustomList() {
		const customListEntries = this.isTitleTypeSeason
			? this.title.show?.customlistEntries
			: this.title.customlistEntries;

		return !!customListEntries?.length;
	}

	get isTvShowTracked() {
		if (this.isShowAvailable) {
			return !!this.title.show?.tvShowTrackingEntry?.createdAt;
		}

		return this.title.objectType === ObjectType.Show ? !!this.title.tvShowTrackingEntry?.createdAt : false;
	}

	get isShowAvailable() {
		return this.isTitleTypeSeason && this.title.show;
	}

	isTitleInList(listName: TitleListType) {
		switch (listName) {
			case TitleListType.WatchNext:
				if (this.isShowAvailable) {
					return !!this.title?.show?.watchlistEntryV2?.createdAt;
				}
				return !!this.title?.watchlistEntryV2?.createdAt;
			case TitleListType.Likelist:
				if (this.isShowAvailable) {
					return !!this.title?.show?.likelistEntry?.createdAt;
				}
				return !!this.title?.likelistEntry?.createdAt;
			case TitleListType.Dislikelist:
				if (this.isShowAvailable) {
					return !!this.title?.show?.dislikelistEntry?.createdAt;
				}
				return !!this.title?.dislikelistEntry?.createdAt;
			case TitleListType.CaughtUp:
				return this.title.objectType === ObjectType.Movie
					? !!this.title?.seenlistEntry?.createdAt
					: this.title?.seenState?.progress === 100;
		}
	}
}
